import React from 'react';
import { ArrowRight } from 'lucide-react';
import { POSITION_ORDER, positionLabel, positionStyle } from './format';

/**
 * The position scale, drawn so it survives greyscale: a five-step ladder with
 * one step filled at the reading, plus the chevron shape for the strength.
 * Colour only reinforces what the shape already says.
 */
export const PositionLadder: React.FC<{ position: number; className?: string }> = ({ position, className = '' }) => {
  const style = positionStyle(position);
  return (
    <span className={`inline-flex items-end gap-[2px] ${className}`} aria-hidden="true">
      {POSITION_ORDER.slice()
        .reverse()
        .map((step) => (
          <span
            key={step}
            className={`block w-[3px] rounded-full ${
              step === Math.round(position) ? `${style.solid} h-3` : 'bg-neutral-200 h-1.5'
            }`}
          />
        ))}
    </span>
  );
};

/** Word + chevron: the everyday label for a position */
export const PositionPill: React.FC<{
  position: number;
  /** Shorten the label on tight rows */
  short?: boolean;
  className?: string;
}> = ({ position, short = false, className = '' }) => {
  const style = positionStyle(position);
  const Icon = style.icon;
  return (
    <span
      className={`inline-flex h-5 items-center gap-1 whitespace-nowrap rounded-full pl-1.5 pr-2 text-[11px] font-medium leading-none ${style.pill} ${className}`}
    >
      <Icon className="h-3 w-3" strokeWidth={2} />
      {short ? style.short : style.label}
    </span>
  );
};

/** Just the shape, for dense rows where the word is elsewhere */
export const PositionGlyph: React.FC<{ position: number; className?: string }> = ({ position, className = '' }) => {
  const style = positionStyle(position);
  const Icon = style.icon;
  return (
    <span
      title={style.label}
      className={`inline-flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full ${style.pill} ${className}`}
    >
      <Icon className="h-3 w-3" strokeWidth={2} />
      <span className="sr-only">{style.label}</span>
    </span>
  );
};

/** "Opposed → Neutral", the shape of a move */
export const PositionChange: React.FC<{ from: number | null; to: number; className?: string }> = ({
  from,
  to,
  className = '',
}) => (
  <span className={`inline-flex flex-wrap items-center gap-1.5 ${className}`}>
    {from === null || from === undefined ? (
      <span className="text-[12px] text-neutral-400">No position</span>
    ) : (
      <PositionPill position={from} short />
    )}
    <ArrowRight className="h-3.5 w-3.5 flex-shrink-0 text-neutral-300" strokeWidth={1.75} />
    <PositionPill position={to} short />
  </span>
);

/** Five-way selector used wherever a position is chosen */
export const PositionPicker: React.FC<{
  value: number;
  onChange: (position: number) => void;
  label: string;
  className?: string;
}> = ({ value, onChange, label, className = '' }) => (
  <div role="radiogroup" aria-label={label} className={`grid grid-cols-5 gap-1 ${className}`}>
    {POSITION_ORDER.map((position) => {
      const style = positionStyle(position);
      const Icon = style.icon;
      const active = position === value;
      return (
        <button
          key={position}
          type="button"
          role="radio"
          aria-checked={active}
          onClick={() => onChange(position)}
          title={style.label}
          className={`flex h-[58px] flex-col items-center justify-center gap-1 rounded-xl px-0.5 text-[10.5px] leading-tight transition-colors sm:px-1 sm:text-[11px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-600/40 ${
            active
              ? `${style.pill} ring-1 ring-inset ring-black/[0.06]`
              : 'bg-neutral-50 text-neutral-500 hover:bg-neutral-100'
          }`}
        >
          <Icon className={`h-4 w-4 ${active ? '' : style.text}`} strokeWidth={2} />
          {/* two short lines rather than "Supporti…" on a phone */}
          <span className="line-clamp-2 w-full break-words text-center">{style.short}</span>
        </button>
      );
    })}
  </div>
);

/**
 * Influence on THIS issue, 0–100. Grey by design: the bar answers "how much
 * weight", never "which side" — that is the position's job.
 */
export const InfluenceBar: React.FC<{
  value: number;
  /** "Directory influence" when no issue-specific figure exists */
  muted?: boolean;
  label?: string;
  className?: string;
}> = ({ value, muted = false, label = 'Influence on this issue', className = '' }) => {
  const pct = Math.max(0, Math.min(100, Math.round(value || 0)));
  return (
    <span className={`flex items-center gap-1.5 ${className}`} title={`${label} ${pct}/100`}>
      <span className="block h-1 flex-1 overflow-hidden rounded-full bg-neutral-100">
        <span
          className={`block h-full rounded-full ${muted ? 'bg-neutral-300' : 'bg-ink'}`}
          style={{ width: `${pct}%` }}
        />
      </span>
      <span className="w-[22px] flex-shrink-0 text-right text-[11px] tabular-nums text-neutral-400">{pct}</span>
      <span className="sr-only">
        {label} {pct} of 100
      </span>
    </span>
  );
};

export { positionLabel };
